/**
 * Energy and Pricing Schemas
 *
 * This module contains schemas related to energy pricing, tariffs, and fees.
 * Separated from main schema file following domain-driven design.
 */

import { z } from 'zod';

// ============================================
// BASIC TYPES
// ============================================

export const MonthlyConsumptionSchema = z.record(z.any(), z.number().nullish()).nullish();
export const YearlyPricesSchema = z.record(z.any(), z.number().nullish()).nullish();

// ============================================
// ENERGY PRICE SCHEMAS
// ============================================

const VALID_ENERGY_PRICE_ORIGINS = ['Manual', 'Indicative', 'Price Projection'] as const;
const VALID_CHARGES_COVERAGE = [
  'FULL_COVERAGE_FOR_ENERGY_CONTRACT',
  'FULL_PASS_THROUGH_OF_COSTS_TO_THE_CUSTOMER',
] as const;

export const EnergyPriceInfoDTOSchema = z.object({
  origin: z.preprocess(
    (val) =>
      VALID_ENERGY_PRICE_ORIGINS.includes(val as (typeof VALID_ENERGY_PRICE_ORIGINS)[number]) ? val : undefined,
    z
      .enum(VALID_ENERGY_PRICE_ORIGINS, {
        errorMap: () => ({ message: 'Campo é obrigatório' }),
      })
      .nullish(),
  ),
  manualAclPricesHasChargesCoverage: z.preprocess(
    (val) => (VALID_CHARGES_COVERAGE.includes(val as (typeof VALID_CHARGES_COVERAGE)[number]) ? val : undefined),
    z
      .enum(VALID_CHARGES_COVERAGE, {
        errorMap: () => ({ message: 'Campo é obrigatório' }),
      })
      .nullish(),
  ),
});

export const RawEnergyPriceProjectionComponentSchema = z.object({
  origin: z
    .enum(['Manual', 'Indicative', 'Price Projection'], {
      errorMap: () => ({ message: 'Campo é obrigatório' }),
    })
    .nullish(),

  submarket: z.string().nullish(),

  energyType: z
    .enum(['INCENTIVIZED_50', 'INCENTIVIZED_100', 'CONVENTIONAL'], {
      errorMap: () => ({ message: 'Campo é obrigatório' }),
    })
    .nullish(),

  rawPriceList: YearlyPricesSchema.nullish(),
});

// ============================================
// FEES AND MARGINS
// ============================================

export const MarginFeeSchema = z.object({
  type: z.string().nullish(),
  values: YearlyPricesSchema.nullish(),
  aux__useSamePricePerYears: z.boolean().default(false).nullish(),
  aux__useUnique: z.number().nullish(),
});

export const GuaranteeTypeSchema = z
  .enum([
    'OTHER',
    'GUARANTEE_LETTER',
    'SURETY_BOND',
    'BANK_GUARANTEE',
    'PROMISSORY_NOTE',
    'REGISTRATION_AGAINST_PAYMENT',
    'ADVANCE_PAYMENT',
  ])
  .nullish();

// ============================================
// PRICE LIST COMPOSITION
// ============================================

export const EnergyPriceComponentSchema = z.object({
  title: z.string(),
  label: z.string(),
  type: z.string(),
  value: z.number(),
  valueByYear: z.record(z.string(), z.number()).nullish(),
  variationValueCeil: z.number().nullish(),
  priceValueCeil: z.number().nullish(),
  variationValueFloor: z.number().nullish(),
  priceValueFloor: z.number().nullish(),
  additionalInfo: z.string().nullish(),
});

export const PriceListCompositionItemSchema = z.object({
  energyPriceComponent: EnergyPriceComponentSchema,
  energyPriceList: z.record(z.string(), z.number()),
});

export const PriceListCompositionSchema = z.array(PriceListCompositionItemSchema).nullish();

// ============================================
// TARIFF SCHEMAS
// ============================================

export const TaxesSchema = z.object({
  icms: z.number().nullish(),
  pis: z.number().nullish(),
  cofins: z.number().nullish(),
});

export const TariffLowVoltageSchema = z.object({
  tusd: z.number().nullish(),
});

export const TariffMediumVoltageSchema = z.object({
  id: z.number().nullish(),
  tariffAdjustmentDate: z.string().nullish(),
  tariffGroup: z.number().nullish(),
  edc: z
    .object({
      id: z.number().nullish(),
      name: z.string().nullish(),
    })
    .nullish(),
  distributionTariffOffPeak: z.number().nullish(),
  distributionTariffPeak: z.number().nullish(),
  electricityTariffOffPeak: z.number().nullish(),
  electricityTariffPeak: z.number().nullish(),
  demandOffPeak: z.number().nullish(),
  demandPeak: z.number().nullish(),
});

export const TariffProjectionComponentSchema = z.object({
  edc: z.union([z.number(), z.string()]).nullish(),
  tariffGroup: z.union([z.number(), z.string()]).nullish(),
  tariffList: z.union([TariffLowVoltageSchema, TariffMediumVoltageSchema]).nullish(),
});

export const ICMSProjectionComponentSchema = z.object({
  edc: z.number().nullish(),
  icmsType: z.number().nullish(),
  taxes: TaxesSchema.nullish(),
});

export const ProjectionComponentsSchema = z.object({
  tariffs: z.array(TariffProjectionComponentSchema).nullish(),
  icmsList: z.array(ICMSProjectionComponentSchema).nullish(),
});
